import { useEffect, useReducer, useRef, useState, type FormEvent } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Warning, ArrowLeft, Key, Minus, Plus } from '@phosphor-icons/react';
import type { AxiosError } from 'axios';
import { errorStatus, extractErrorMessage } from '../lib/error';
import { formatPrice } from '../lib/format';
import { useBookingQuote, useConfirmBooking } from '../hooks/useBooking';
import { requestBookingOtp, type ConfirmBookingBody } from '../services/api/bookings.api';
import { useMutation } from '@tanstack/react-query';
import { FormField, controlClass } from '../components/listing/FormField';
import { StaySummary } from '../components/booking/StaySummary';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type GuestField = 'fullName' | 'email' | 'phone' | 'specialRequests' | 'guestNames' | 'arrivalTime';
type Values = Record<GuestField, string> & { companyWebsite: string };
type Errors = Partial<Record<GuestField, string>>;

const EMPTY: Values = {
  fullName: '', email: '', phone: '', specialRequests: '', guestNames: '', arrivalTime: '', companyWebsite: '',
};

/** Estimated arrival windows (OTA-standard). Empty value ⇒ "Not sure yet" ⇒ omitted from the payload. */
const ARRIVAL_OPTIONS = [
  { value: '', label: 'Not sure yet' },
  { value: 'Before 12:00 PM', label: 'Before 12:00 PM' },
  { value: '12:00–3:00 PM', label: '12:00–3:00 PM' },
  { value: '3:00–6:00 PM', label: '3:00–6:00 PM' },
  { value: '6:00–9:00 PM', label: '6:00–9:00 PM' },
  { value: 'After 9:00 PM', label: 'After 9:00 PM' },
];

/** Client resend cooldown — mirrors GuestLoginPage / the leads OtpVerifyPanel + backend throttle. */
const COOLDOWN_SECONDS = 30;

function validate(values: Values): Errors {
  const e: Errors = {};
  if (!values.fullName.trim()) e.fullName = 'Your name is required.';
  if (!values.email.trim()) e.email = 'Email is required.';
  else if (!EMAIL_RE.test(values.email.trim())) e.email = 'Enter a valid email address.';
  if (!values.phone.trim()) e.phone = 'Phone is required.';
  else if (values.phone.trim().length < 3) e.phone = 'Enter a valid phone number.';
  return e;
}

type Action = { type: 'set'; field: keyof Values; value: string } | { type: 'errors'; errors: Errors };
function reducer(s: { values: Values; errors: Errors }, a: Action) {
  if (a.type === 'set') return { values: { ...s.values, [a.field]: a.value }, errors: { ...s.errors, [a.field]: undefined } };
  return { ...s, errors: a.errors };
}

/**
 * Booking funnel (/book/:id) — guest-first checkout. Quote on load → guest details
 * (party composition + check-in details) → email OTP verification → confirm
 * (Idempotency-Key) → /booking/:ref. See docs/booking-funnel/.
 */
export default function BookingFunnelPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const unitId = params.get('unitId') ?? '';
  const checkIn = params.get('checkIn') ?? '';
  const checkOut = params.get('checkOut') ?? '';
  const paramsValid = !!id && !!unitId && !!checkIn && !!checkOut && checkOut > checkIn;

  // Party composition (OTA-standard). `guests` is authoritative = adults + children.
  const [adults, setAdults] = useState(() => Math.min(50, Math.max(1, Number(params.get('guests')) || 2)));
  const [children, setChildren] = useState(0);
  const totalGuests = adults + children;

  // Promo code: `promoInput` is the live field; `promoCode` is the applied value
  // that's actually sent to the server (only changes on Apply, so we don't re-quote per keystroke).
  const [promoInput, setPromoInput] = useState('');
  const [promoCode, setPromoCode] = useState('');

  // Re-quotes whenever guests (adults+children) or the applied promo changes — the queryKey
  // carries totalGuests, so quotedTotal always matches a guests=adults+children quote.
  const quoteBody = { propertyId: id ?? '', unitId, checkIn, checkOut, guests: totalGuests, ...(promoCode && { promoCode }) };
  const quoteQ = useBookingQuote(quoteBody, paramsValid);
  const confirm = useConfirmBooking();

  const [form, dispatch] = useReducer(reducer, { values: EMPTY, errors: {} });
  const [priceChanged, setPriceChanged] = useState<number | null>(null);
  const [conflict, setConflict] = useState(false);

  // Two-step: 'details' (form) → 'verify' (email OTP).
  const [step, setStep] = useState<'details' | 'verify'>('details');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [resendConfirm, setResendConfirm] = useState<string | null>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  const otpRequest = useMutation({ mutationFn: (email: string) => requestBookingOtp(email) });

  // One Idempotency-Key per intent; regenerated when the payload changes (edits / price re-ack).
  const keyRef = useRef<string>(crypto.randomUUID());
  const keyDirty = useRef(false);
  const set = (field: keyof Values) => (v: string) => { keyDirty.current = true; dispatch({ type: 'set', field, value: v }); };
  const setParty = (fn: () => void) => { keyDirty.current = true; fn(); };

  // Single cooldown interval, cleared on unmount and when it hits 0.
  useEffect(() => {
    if (cooldown <= 0) return;
    const int = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(int);
  }, [cooldown]);

  // Focus the code input when we land on the verify step.
  useEffect(() => { if (step === 'verify') otpRef.current?.focus(); }, [step]);

  if (!paramsValid) {
    return (
      <Centered icon={<Warning className="h-12 w-12 text-red-500" />} title="Your selection expired">
        <Link to={`/hotel/${id ?? ''}`} className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue-dark">
          <ArrowLeft className="h-4 w-4" /> Back to property
        </Link>
      </Centered>
    );
  }

  // Step A → request an emailed code, then advance to verification.
  function continueToVerify(e: FormEvent) {
    e.preventDefault();
    const errors = validate(form.values);
    if (Object.keys(errors).length) { dispatch({ type: 'errors', errors }); return; }
    if (!quoteQ.data?.available) return;
    setOtp('');
    setOtpError(null);
    setResendConfirm(null);
    otpRequest.mutate(form.values.email.trim(), {
      onSuccess: (data) => {
        setStep('verify');
        if (data.devCode) setOtp(data.devCode); // non-prod convenience only
      },
    });
  }

  function handleResend() {
    if (otpRequest.isPending || cooldown > 0) return;
    setResendConfirm(null);
    setOtpError(null);
    otpRequest.mutate(form.values.email.trim(), {
      onSuccess: (data) => {
        setOtp(data.devCode ?? '');
        setResendConfirm(`We sent a new code to ${form.values.email.trim()}.`);
        setCooldown(COOLDOWN_SECONDS);
        otpRef.current?.focus();
      },
      // Only lock the button on a genuine rate-limit; a transient error means no
      // code went out, so the guest should be able to retry at once.
      onError: (err) => { if (errorStatus(err) === 429) setCooldown(COOLDOWN_SECONDS); },
    });
  }

  // Step B → confirm the booking with the full body (incl. otp + guests=adults+children).
  function confirmBookingNow(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setOtpError('Enter the 6-digit code we emailed you.'); return; }
    if (!quoteQ.data) return;
    setConflict(false);
    setOtpError(null);

    if (keyDirty.current) { keyRef.current = crypto.randomUUID(); keyDirty.current = false; }

    const body: ConfirmBookingBody = {
      propertyId: quoteBody.propertyId, unitId, checkIn, checkOut,
      guests: totalGuests, adults, children,
      quotedTotal: priceChanged ?? quoteQ.data.total,
      guest: { fullName: form.values.fullName.trim(), email: form.values.email.trim(), phone: form.values.phone.trim() },
      ...(form.values.guestNames.trim() && { guestNames: form.values.guestNames.trim() }),
      ...(form.values.arrivalTime && { arrivalTime: form.values.arrivalTime }),
      ...(form.values.specialRequests.trim() && { specialRequests: form.values.specialRequests.trim() }),
      ...(promoCode && { promoCode }),
      companyWebsite: form.values.companyWebsite,
      otp,
    };

    confirm.mutate(
      { body, idempotencyKey: keyRef.current },
      {
        onSuccess: (conf) => navigate(`/booking/${conf.reference}`, { state: conf }),
        onError: (err) => {
          const s = errorStatus(err);
          const data = (err as AxiosError<{ code?: string; total?: number }>)?.response?.data;
          if (s === 401) {
            setOtpError('That code is invalid or expired — check the code or resend.');
            otpRef.current?.focus();
          } else if (s === 409 && data?.code === 'PRICE_CHANGED' && typeof data.total === 'number') {
            setPriceChanged(data.total);
            keyDirty.current = true; // re-ack is a new intent
          } else if (s === 409 && data?.code === 'UNIT_UNAVAILABLE') {
            setConflict(true);
          }
        },
      },
    );
  }

  function backToDetails() {
    setStep('details');
    setConflict(false);
    confirm.reset();
    setOtpError(null);
    setResendConfirm(null);
  }

  const status = errorStatus(confirm.error);
  const banner = confirm.isError && !conflict && !priceChanged && status !== 401
    ? (status === 429 ? 'Too many requests — please wait a moment and try again.' : extractErrorMessage(confirm.error))
    : null;
  const reqErr = otpRequest.isError
    ? (errorStatus(otpRequest.error) === 429 ? 'Please wait a moment before requesting another code.' : extractErrorMessage(otpRequest.error))
    : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="text-2xl font-bold text-brand-navy">Confirm your booking</h1>
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
        {step === 'details' ? (
          /* Guest details */
          <form onSubmit={continueToVerify} noValidate className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h2 className="text-lg font-bold text-brand-navy">Your details</h2>
            <p className="mt-1 text-sm text-gray-500">No account needed — we'll email a 6-digit code to confirm it's you.</p>

            {conflict && (
              <div role="alert" className="mt-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
                This room was just booked for those dates.{' '}
                <Link to={`/hotel/${id}?${params.toString()}`} className="font-semibold text-brand-blue hover:underline">See other rooms</Link>
              </div>
            )}
            {reqErr && <div role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{reqErr}</div>}

            {/* Party composition */}
            <div className="mt-5 grid grid-cols-2 gap-5">
              <Stepper id="adults" label="Adults" value={adults} min={1} max={50}
                onChange={(v) => setParty(() => setAdults(v))} />
              <Stepper id="children" label="Children" value={children} min={0} max={20}
                onChange={(v) => setParty(() => setChildren(v))} />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FormField id="fullName" label="Full name" required error={form.errors.fullName}>
                <input id="fullName" name="fullName" type="text" autoComplete="name" value={form.values.fullName}
                  onChange={(e) => set('fullName')(e.target.value)} aria-invalid={!!form.errors.fullName}
                  aria-describedby={form.errors.fullName ? 'fullName-error' : undefined} className={controlClass(!!form.errors.fullName)} />
              </FormField>
              <FormField id="email" label="Email" required error={form.errors.email}>
                <input id="email" name="email" type="email" autoComplete="email" value={form.values.email}
                  onChange={(e) => set('email')(e.target.value)} aria-invalid={!!form.errors.email}
                  aria-describedby={form.errors.email ? 'email-error' : undefined} className={controlClass(!!form.errors.email)} />
              </FormField>
              <FormField id="phone" label="Phone" required error={form.errors.phone}>
                <input id="phone" name="phone" type="tel" autoComplete="tel" value={form.values.phone}
                  onChange={(e) => set('phone')(e.target.value)} aria-invalid={!!form.errors.phone}
                  aria-describedby={form.errors.phone ? 'phone-error' : undefined} className={controlClass(!!form.errors.phone)} />
              </FormField>
              <FormField id="arrivalTime" label="Estimated arrival time" hint="Optional">
                <select id="arrivalTime" name="arrivalTime" value={form.values.arrivalTime}
                  onChange={(e) => set('arrivalTime')(e.target.value)} className={controlClass(false)}>
                  {ARRIVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </FormField>
            </div>

            <div className="mt-5">
              <FormField id="guestNames" label="Names of other guests" hint="Optional — helps the property check everyone in">
                <textarea id="guestNames" name="guestNames" rows={2} maxLength={500} value={form.values.guestNames}
                  onChange={(e) => set('guestNames')(e.target.value)}
                  aria-describedby="guestNames-hint" className={controlClass(false)} />
              </FormField>
            </div>
            <div className="mt-5">
              <FormField id="specialRequests" label="Special requests" hint="Optional">
                <textarea id="specialRequests" name="specialRequests" rows={3} maxLength={2000} value={form.values.specialRequests}
                  onChange={(e) => set('specialRequests')(e.target.value)} className={controlClass(false)} />
              </FormField>
            </div>

            {/* Honeypot */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="company_website">Company website</label>
              <input id="company_website" name="company_website" type="text" tabIndex={-1} autoComplete="off"
                value={form.values.companyWebsite} onChange={(e) => set('companyWebsite')(e.target.value)} />
            </div>

            <button type="submit" disabled={otpRequest.isPending || quoteQ.isPending || !quoteQ.data?.available}
              className="mt-6 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white transition hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
              {otpRequest.isPending ? 'Sending code…' : 'Continue to verification'}
            </button>
          </form>
        ) : (
          /* Email OTP verification */
          <form onSubmit={confirmBookingNow} noValidate className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8">
            <Key className="h-10 w-10 text-brand-blue" aria-hidden="true" />
            <h2 className="mt-3 text-lg font-bold text-brand-navy">Verify your email</h2>
            <p className="mt-1 text-sm text-gray-500">
              We've emailed a 6-digit code to <strong>{form.values.email.trim()}</strong>.
            </p>

            {conflict && (
              <div role="alert" className="mt-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
                This room was just booked for those dates.{' '}
                <Link to={`/hotel/${id}?${params.toString()}`} className="font-semibold text-brand-blue hover:underline">See other rooms</Link>
              </div>
            )}
            {banner && <div role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{banner}</div>}

            <div className="mt-6">
              <label htmlFor="otp" className="mb-2 block text-sm font-semibold text-brand-navy">
                Enter the code
              </label>
              <input
                ref={otpRef}
                id="otp"
                name="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="––––––"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setOtpError(null); }}
                aria-invalid={!!otpError}
                aria-describedby={otpError ? 'otp-error' : undefined}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-2xl font-semibold tracking-[0.6em] text-gray-900 placeholder:text-gray-300 outline-none focus-visible:border-brand-blue focus-visible:ring-2 focus-visible:ring-brand-blue/40 sm:w-64"
              />
              {otpError && <p id="otp-error" role="alert" className="mt-2 text-xs font-medium text-red-600">{otpError}</p>}
            </div>

            {resendConfirm && !reqErr && (
              <p role="status" className="mt-3 text-sm font-medium text-brand-navy">{resendConfirm}</p>
            )}
            {reqErr && <p role="alert" className="mt-3 text-sm font-medium text-red-600">{reqErr}</p>}

            <button type="submit" disabled={confirm.isPending || otp.length !== 6}
              className="mt-6 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white transition hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
              {confirm.isPending ? 'Confirming…' : 'Confirm booking'}
            </button>

            <p className="mt-3 text-xs text-gray-500">The code expires in 10 minutes.</p>

            <div className="mt-4 flex items-center justify-between text-sm">
              <button type="button" onClick={backToDetails}
                className="inline-flex items-center gap-1 font-semibold text-brand-blue hover:underline">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
              </button>
              <button type="button" disabled={otpRequest.isPending || cooldown > 0} onClick={handleResend}
                className="font-semibold text-brand-blue hover:underline disabled:cursor-not-allowed disabled:opacity-60">
                {otpRequest.isPending ? 'Sending…' : cooldown > 0 ? `Resend available in 0:${String(cooldown).padStart(2, '0')}` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* Stay summary */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          {quoteQ.isPending ? (
            <div className="h-64 animate-pulse rounded-2xl bg-gray-100" />
          ) : quoteQ.isError ? (
            <div className="rounded-2xl bg-red-50 p-6 text-center">
              <p className="font-semibold text-brand-navy">Couldn't price this stay.</p>
              <button type="button" onClick={() => quoteQ.refetch()} className="mt-3 rounded-xl bg-brand-blue px-5 py-2 text-sm font-semibold text-white">Retry</button>
            </div>
          ) : quoteQ.data && !quoteQ.data.available ? (
            <div className="rounded-2xl bg-amber-50 p-6 text-center">
              <p className="font-semibold text-brand-navy">Not available for these dates.</p>
              <Link to={`/hotel/${id}?${params.toString()}`} className="mt-3 inline-block text-sm font-semibold text-brand-blue hover:underline">See other rooms</Link>
            </div>
          ) : quoteQ.data ? (
            <StaySummary
              quote={quoteQ.data}
              party={{ adults, children }}
              promo={{
                value: promoInput,
                onChange: setPromoInput,
                onApply: (code) => { setPromoInput(code); setPromoCode(code); },
                onClear: () => { setPromoInput(''); setPromoCode(''); },
                pending: quoteQ.isFetching,
              }}
            />
          ) : null}
        </aside>
      </div>

      {/* Price-change modal */}
      {priceChanged != null && quoteQ.data && (
        <div role="dialog" aria-modal="true" aria-label="Price updated" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold text-brand-navy">The price updated</h2>
            <p className="mt-2 text-sm text-gray-600">The total for your dates is now <strong>{formatPrice(priceChanged, quoteQ.data.currency)}</strong> (was {formatPrice(quoteQ.data.total, quoteQ.data.currency)}). Continue at the new price?</p>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setPriceChanged(null)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-brand-navy">Cancel</button>
              <button type="button" onClick={(e) => confirmBookingNow(e as unknown as FormEvent)} className="rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue-dark">Review &amp; continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Accessible +/− number stepper (clamped to [min, max]); readout is the labelled control. */
function Stepper({ id, label, value, min, max, onChange, hint }: {
  id: string; label: string; value: number; min: number; max: number; onChange: (v: number) => void; hint?: string;
}) {
  const btn = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-brand-navy transition hover:bg-brand-blue-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <FormField id={id} label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <button type="button" aria-label={`Decrease ${label.toLowerCase()}`} disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))} className={btn}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input id={id} name={id} type="number" inputMode="numeric" min={min} max={max} value={value} readOnly
          aria-live="polite"
          className="h-10 w-14 rounded-lg border border-gray-300 text-center font-semibold text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
        <button type="button" aria-label={`Increase ${label.toLowerCase()}`} disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))} className={btn}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </FormField>
  );
}

function Centered({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <div className="mx-auto">{icon}</div>
      <h1 className="mt-4 text-xl font-bold text-brand-navy">{title}</h1>
      {children}
    </div>
  );
}
